import { PrivacyPolicyPagesEnum, isProduction } from "@/utils/const";
import Global from "./global";
import React from "react";
import { Logger } from "next-axiom";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration"
dayjs.extend(duration)

// uuid 工具函数
export const generateUuid = () => {
  var s = [];
  var hexDigits = "0123456789abcdef";
  for (var i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits[(parseInt(s[19], 16) & 0x3) | 0x8]; // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-";

  var uuid = s.join("");
  return uuid;
};

// url 截取，开发环境不需要全路径
export const changeUrl = (url: string) => {
  return process.env.NEXT_PUBLIC_ISDev === "true"
    ? url
      .replace("https://www.bravve.cn/", "http://localhost:3000/")
      .replace("http://www.bravve.cn/", "http://localhost:3000/")
      .replace(Global.domainUrl as string, "http://localhost:3000/")
    : url;
};

export const isEmail = (email: string) => {
  const reg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return reg.test(email);
};

// 使用 link 标签（只加载但不显示）
export const preloadImage = (url: string) => {
  if (document.getElementById(url)) return; // 如果已经存在相同的 link 标签，则不再添加
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.id = url;
  document.head.appendChild(link); // 将链接标签添加到页面的 head 部分
};

// 图片包含 % 等特殊字符时，需要使用 encodeURIComponent
export const handleUrlSpecialChar = (url: string) => {
  // console.log('url: ', url)
  return url; // url.includes("%-") ? encodeURIComponent(url) : url
};

// 定义节流函数
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
  immediate: boolean = false,
): (...args: Parameters<T>) => void {
  let lastCall = 0; // 上一次执行的时间戳
  let timer: ReturnType<typeof setTimeout> | null = null; // 定时器引用

  return function (...args: Parameters<T>) {
    const now = Date.now(); // 当前时间戳

    if (immediate && lastCall === 0) {
      // 如果启用了立即执行，且尚未调用过
      func(...args); // 立即执行
      lastCall = now; // 更新上一次调用时间
      return;
    }

    if (now - lastCall >= delay) {
      // 如果超出时间间隔，执行函数
      if (timer) {
        clearTimeout(timer); // 清理定时器
        timer = null;
      }
      lastCall = now; // 更新上一次调用时间
      func(...args); // 执行目标函数
    } else if (!timer) {
      // 如果在间隔时间内且没有定时器，设置延迟执行
      timer = setTimeout(
        () => {
          lastCall = Date.now(); // 更新上一次调用时间
          func(...args); // 延迟执行目标函数
          timer = null; // 清理定时器
        },
        delay - (now - lastCall),
      );
    }
  };
}

// 防抖函数实现
export function debounce(fn: Function, delay: number) {
  let timer: NodeJS.Timeout | null = null;

  return function (this: any, ...args: any[]) {
    if (timer) {
      clearTimeout(timer); // 清除上一次的定时器
    }

    // 设置新的定时器
    timer = setTimeout(() => {
      fn.apply(this, args); // 执行回调函数
    }, delay);
  };
}

/**
 * 打开隐私政策页面
 * @param type
 */
export const openPricacyPolicy = (type: PrivacyPolicyPagesEnum) => {
  const queryString = new URLSearchParams({
    type,
  }).toString();
  // 将 Next.js 路由转换为完整的 URL
  if (window) {
    const fullUrl = `${process.env.NEXT_PUBLIC_DOMAIN_URL}privacy-policy?${queryString}`;
    window.location.href = fullUrl;
  }
};

/**
 * 返回隐私政策url
 * @param type
 */
export const returnPricacyPolicyUrl = (type: PrivacyPolicyPagesEnum) => {
  const queryString = new URLSearchParams({
    type,
  }).toString();
  // 将 Next.js 路由转换为完整的 URL
  const fullUrl = `${process.env.NEXT_PUBLIC_DOMAIN_URL}privacy-policy?${queryString}`;
  return fullUrl;
};

/**
 * 全局处理图片
 */
export const handleImgUrl = (url: string, size: string) => {
  // 如果以 .png 结尾，则直接返回原图
  if (url.endsWith(".png")) {
    return url;
  }
  // 匹配文件名的正则表达式，捕获扩展名
  const regex = /(.*\/[^/]+)(\.[a-zA-Z0-9]+)$/;
  // 使用正则替换
  // const modifiedUrl = url.split('.webp'); // url.replace(regex, `$1-${size}$2`);
  const attr = url.split(".webp");

  return attr.join(`-${size}`) + ".webp";
};

export const isEveryFieldFilled = (array: Array<object>) => {
  if (!Array.isArray(array)) {
    return;
  }

  return array.every((obj) => {
    if (!obj) {
      return;
    }

    return Object.values(obj).every(
      (value) => value !== null && value !== undefined && value !== "",
    );
  });
};

export const isObjectEmpty = (obj: object) => {
  return Object.keys(obj).length === 0;
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return;

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// js 判断当前url 是否为首页
export const isHomePage = () => {
  const path = window.location.pathname;
  const hash = window.location.hash;

  // 判断路径是否为 "/" 或 "/index.html"，且哈希为空或为 "#/"
  return (
    (path === "/" || path === "/index.html") && (hash === "" || hash === "#/")
  );
  // // 判断路径是否为 "/" 或 "/index.html"
  // return path === '/' || path === '/index.html';
};

//
const loginGtag = (
  e: React.MouseEvent<HTMLElement, MouseEvent>,
  user_id?: any,
) => {
  const { contentPosition, contentId, contentName } = (e.target as HTMLElement)
    .dataset;

  const cardItemDom = (e.target as HTMLElement).closest(
    ".cardItem",
  ) as HTMLElement | null;
  if (cardItemDom && cardItemDom.dataset) {
    const { cardType, cardPosition } = cardItemDom.dataset;
    const data = {
      content_id: (cardType || "") + cardPosition + "" + contentId,
      content_name: contentName,
      content_position: contentPosition,
      exposure_time: Date.now(),
      user_id,
    };
    if (user_id) {
      data.user_id = user_id;
    }
    // @ts-ignore
    window.gtag("event", "card_content_click", data);
  }
};

const exceptLoginGtag = (
  e: React.MouseEvent<HTMLElement, MouseEvent>,
  linkData: { link?: string; url?: string },
) => {
  const userInfoStr = globalThis?.localStorage?.getItem("userInfo");
  let userInfo;
  if (userInfoStr) {
    userInfo = JSON.parse(userInfoStr);
  }
  const { contentPosition, contentId, contentName } = (e.target as HTMLElement)
    .dataset;
  const cardItemDom = (e.target as HTMLElement).closest(
    ".cardItem",
  ) as HTMLElement | null;
  if (cardItemDom && cardItemDom.dataset) {
    const { cardType, cardPosition } = cardItemDom.dataset;
    // @ts-ignore
    window.gtag("event", "card_content_click", {
      content_id: (cardType || "") + cardPosition + "" + contentId,
      content_name: contentName,
      content_position: contentPosition,
      exposure_time: Date.now(),
      user_id: userInfo?.id,
    });
  }
};

// 处理 卡片跳转的问题
export const handleCardClick = (
  linkData: {
    link_type?: string | number;
    link?: string;
    open_type?: string | number;
  } & any,
  node: any,
  isMobile: boolean,
) => {
  // 跳转 注册 登录
  if (linkData?.link_type === 2) {
    const userInfoStr = globalThis?.localStorage?.getItem("userInfo");
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      if (userInfo?.email) {
        return React.createElement(
          "div",
          {
            onClick: (e) => {
              loginGtag(e, userInfo?.id);
            },
            key: linkData.idx,
            className: "cardItemContent",
            "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
            "data-content-position": linkData.idx,
            "data-content-name":
              linkData.pic_name ||
              linkData.name ||
              linkData.tabName ||
              linkData.productName ||
              linkData.icon_name ||
              linkData.alt_text ||
              linkData.pictureAlt,
          },
          node,
        );
      }
    }
    return React.createElement(
      "div",
      {
        onClick: (e) => {
          globalThis.document.getElementById("signInBtn")?.click();
          loginGtag(e);
        },
        key: linkData.idx,
        className: "cursor-pointer cardItemContent",
        "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
        "data-content-position": linkData.idx,
        "data-content-name":
          linkData.pic_name ||
          linkData.name ||
          linkData.tabName ||
          linkData.productName ||
          linkData.icon_name ||
          linkData.alt_text ||
          linkData.pictureAlt,
      },
      node,
    );
  } else {
    if (linkData?.link) {
      return React.createElement(
        "a",
        {
          href: linkData.link,
          target: linkData.open_type == 1 ? "_self" : "_blank",
          key: linkData.idx,
          className: "cardItemContent",
          onClick: (e) => {
            exceptLoginGtag(e, linkData);
          },
          "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
          "data-content-position": linkData.idx,
          "data-content-name":
            linkData.pic_name ||
            linkData.name ||
            linkData.tabName ||
            linkData.productName ||
            linkData.icon_name ||
            linkData.alt_text ||
            linkData.pictureAlt,
        },
        node,
      );
    }
    return React.createElement(
      "span",
      {
        onClick: (e) => {
          exceptLoginGtag(e, linkData);
        },
        key: linkData.idx,
        className: "cardItemContent",
        "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
        "data-content-position": linkData.idx,
        "data-content-name":
          linkData.pic_name ||
          linkData.name ||
          linkData.tabName ||
          linkData.productName ||
          linkData.icon_name ||
          linkData.alt_text ||
          linkData.pictureAlt,
      },
      node,
    );
  }
};

const get_click_area = (target: HTMLElement) => {
  // 获取目标节点的 nodeType 属性值
  const nodeType = target.nodeType;
  if (nodeType === Node.TEXT_NODE) {
    return "title";
  } else {
    if (target.tagName === "img") {
      return "image";
    } else {
      return "button";
    }
  }
};

export const handleCardClick1 = (
  linkData: {
    type?: string;
    url?: string;
    target?: string;
  } & any,
  node: any,
  isMobile: boolean,
) => {
  // 跳转 注册 登录
  if (linkData?.type === "userRegister") {
    const userInfoStr = globalThis?.localStorage?.getItem("userInfo");

    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      if (userInfo?.email) {
        return React.createElement(
          "div",
          {
            onClick: (e) => {
              loginGtag(e);
            },
            key: linkData.idx,
            className: "cardItemContent",
            "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
            "data-content-position": linkData.idx,
            "data-content-name":
              linkData.pic_name ||
              linkData.name ||
              linkData.tabName ||
              linkData.productName ||
              linkData.icon_name ||
              linkData.alt_text ||
              linkData.pictureAlt,
          },
          node,
        );
      }
    }
    return React.createElement(
      "div",
      {
        onClick: (e) => {
          globalThis.document.getElementById("signInBtn")?.click();
          loginGtag(e);
        },
        key: linkData.idx,
        className: "cursor-pointer cardItemContent",
        "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
        "data-content-position": linkData.idx,
        "data-content-name":
          linkData.pic_name ||
          linkData.name ||
          linkData.tabName ||
          linkData.productName ||
          linkData.icon_name ||
          linkData.alt_text ||
          linkData.pictureAlt,
      },
      node,
    );
  } else {
    if (linkData?.url) {
      return React.createElement(
        "a",
        {
          href: linkData.url,
          target: linkData.target == "_self" ? "_self" : "_blank",
          key: linkData.idx,
          className: "cardItemContent",
          onClick: (e) => {
            exceptLoginGtag(e, linkData);
          },
          "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
          "data-content-position": linkData.idx,
          "data-content-name":
            linkData.pic_name ||
            linkData.name ||
            linkData.tabName ||
            linkData.productName ||
            linkData.icon_name ||
            linkData.alt_text ||
            linkData.pictureAlt,
        },
        node,
      );
    }
    return React.createElement(
      "span",
      {
        key: linkData.idx,
        className: "cardItemContent",
        onClick: (e) => {
          exceptLoginGtag(e, linkData);
        },
        "data-content-id": isMobile ? "mobile" : "pc" + linkData.idx,
        "data-content-position": linkData.idx,
        "data-content-name":
          linkData.pic_name ||
          linkData.name ||
          linkData.tabName ||
          linkData.productName ||
          linkData.icon_name ||
          linkData.alt_text ||
          linkData.pictureAlt,
      },
      node,
    );
  }
};

export const hasContent = (html: any) => {
  if (html) {
    const text = html.replace(/<[^>]*>|&nbsp;/g, "").trim(); // 去除HTML标签和空格
    return text.length > 0; // 判断是否有实际内容
  }

  return false;
};

/**
 * 格式化剩余时间（毫秒）为 HH:mm:ss 格式
 * 如果剩余时间小于等于0，则返回 '00:00:00'
 */
export const formatTime = (ms: number): string => {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * 格式化到期时间 为 DD:HH:mm:ss
 */
export const calculateTimeDifference = (a: number, b: number) => {
  if (!a || !b) {
    return { days: '00', hours: '00', minutes: '00', seconds: "00" };
  }

  const diff = Math.abs(a - b); // 获取时间差的绝对值

  const days = Math.floor(diff / (1000 * 60 * 60 * 24)).toString().padStart(2, "0");
  let remainingMs = diff % (1000 * 60 * 60 * 24);

  const hours = Math.floor(remainingMs / (1000 * 60 * 60)).toString().padStart(2, "0");
  remainingMs %= (1000 * 60 * 60);

  const minutes = Math.floor(remainingMs / (1000 * 60)).toString().padStart(2, "0");
  remainingMs %= (1000 * 60);

  const seconds = (Math.floor(remainingMs / 1000)).toString().padStart(2, "0");

  return { days, hours, minutes, seconds };
};


// 谷歌分析
export const addGtag = (event_name: string, params: object, type = "event") => {
  if (window) {
    (window as any)?.gtag(type, event_name, params);
  }
};

// 脱敏数据
export const desensitizeEmail = (email: string) => {
  if (!email || !email.includes("@")) {
    return email; // 非法邮箱直接返回原值
  }

  const [username, domain] = email.split("@");
  if (username.length <= 3) {
    return email; // 用户名长度不足3位时不脱敏
  }
  const visibleUsername = username.slice(0, 3) + "****"; // 保留前3位，中间4位用星号替换

  return visibleUsername + "@" + domain;
};

// 错误日志上报
export const reportInofOrError = (data: {
  type: "info" | "error";
  title: LoggerTitleTypeEnum,
  data: {
    status?: number
    message?: string
    data?: any
  }
}) => {
  // 上报到 axiom
  if (!isProduction) {
    const log = new Logger();
    if (data.type === "error") {
      log.error(data.title, { ...data.data });
    } else if (data.type === "info") {
      log.info(data.title, { ...data.data });
    } else {
      return;
    }
    log.flush();
  } else {
    return null;
  }
};

// 日志类型
export enum LoggerTitleTypeEnum {
  /**接口返回数据格式错误 */
  InterfaceReturnDataFormatError = "Interface Return Data Format Error",
  /**水合错误 */
  HydrationError = "Interface Return Data Format Error",
  /**接口非200错误 */
  InterfaceNon200Error = "Interface non-200 error",
  /**接口Response错误 */
  InterfaceResponseError = "Interface Response Error",
  /**客户端 try catch 错误 */
  ClientTryCatchRrror = "ClientTryCatchRrror",

  /**服务端 请求信息 */
  ServerRequestInfo = "ServerRequestInfo",
}

//
export function toQueryString(searchParams: Object): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(searchParams)) {
    if (value !== undefined && value !== null) {
      // 处理数组（如 `tags=1&tags=2`）
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, String(v)));
      } else {
        params.append(key, String(value));
      }
    }
  }

  return params.toString(); // 返回 "key=value&key=value"
}

//
// export function extractContent(text: string): string | null {
//   const prefix = "*#card-start#**#cfg-start#*";
//   const suffix = "*#cfg-end#**#card-end#*";

//   // 创建动态正则表达式（注意转义特殊字符）
//   const pattern = new RegExp(
//     `^${prefix.replace(/\*/g, '\\*')}(.*?)${suffix.replace(/\*/g, '\\*')}$`
//   );

//   const match = text.match(pattern);
//   return match ? match[1] : null;
// }

// 提取内容 
export function extractContent(text: string): {
  isCard: boolean;
  txt: string;
} {
  const prefix = "*#card-start#**#cfg-start#*";
  const suffix = "*#cfg-end#**#card-end#*";

  const prefix1 = "*#text-start#*"
  const suffix1 = "*#text-end#*"

  if (text.startsWith(prefix) && text.endsWith(suffix)) {
    return {
      isCard: true,
      txt: text.substring(prefix.length, text.length - suffix.length)
    }
  } else if (text.startsWith(prefix1) && text.endsWith(suffix1)) {
    return {
      isCard: false,
      txt: text.substring(prefix1.length, text.length - suffix1.length)
    }
  }
  return {
    isCard: false,
    txt: text
  };
}